import React, { useEffect, useMemo, useState } from "react";
import { Form, ProgressBar } from "react-bootstrap";
import { PasswordInput } from "./PasswordInput";

/**
 * Fortaleza aproximada (0–5). Es solo una guía visual: la validación real la
 * hace Django (AUTH_PASSWORD_VALIDATORS: mínimo 8, no común, no solo numérica,
 * no similar a datos del usuario). Por eso una contraseña solo-dígitos se topa
 * en "Débil" aunque sea larga — Django la rechazaría.
 */
function passwordStrength(pw) {
  if (!pw) return { score: 0, label: "", variant: "secondary" };
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (/^\d+$/.test(pw)) score = Math.min(score, 1);

  const levels = [
    { label: "Muy débil", variant: "danger" },
    { label: "Débil", variant: "danger" },
    { label: "Aceptable", variant: "warning" },
    { label: "Buena", variant: "info" },
    { label: "Fuerte", variant: "success" },
    { label: "Fuerte", variant: "success" },
  ];
  return { score, ...levels[score] };
}

/**
 * NewPasswordFields — par de campos "contraseña + confirmar" con medidor de
 * fortaleza y verificación de coincidencia en vivo. Para registro y reset.
 *
 * El padre controla la contraseña (`value` / `onChange`) y recibe la validez
 * combinada (largo ≥ 8 y coincide con la confirmación) vía `onValidityChange`,
 * para habilitar/deshabilitar su botón de envío. La confirmación es estado
 * interno: en registro es solo del cliente; en reset, como Djoser exige
 * `re_new_password`, basta con reenviar `value` (este componente garantiza que
 * son iguales cuando `valid` es true).
 */
function NewPasswordFields({
  value,
  onChange,
  onValidityChange,
  passwordLabel = "Contraseña",
  confirmLabel = "Confirmar contraseña",
  autoComplete = "new-password",
  errorText,
  passwordId = "newPassword",
  confirmId = "confirmPassword",
}) {
  const [confirm, setConfirm] = useState("");

  const strength = useMemo(() => passwordStrength(value), [value]);
  const tooShort = value.length > 0 && value.length < 8;
  const mismatch = confirm.length > 0 && value !== confirm;
  const matches = confirm.length > 0 && value === confirm;
  const valid = value.length >= 8 && value === confirm;

  useEffect(() => {
    if (onValidityChange) onValidityChange(valid);
  }, [valid, onValidityChange]);

  return (
    <>
      <Form.Group className="mb-1" controlId={passwordId}>
        <Form.Label>{passwordLabel}</Form.Label>
        <PasswordInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          minLength={8}
          autoComplete={autoComplete}
          placeholder="Mínimo 8 caracteres"
          isInvalid={tooShort || Boolean(errorText)}
          errorText={
            errorText ||
            (tooShort ? "Debe tener al menos 8 caracteres." : undefined)
          }
        />
      </Form.Group>

      <div className="mb-3" style={{ minHeight: "1.5rem" }}>
        {value.length > 0 && (
          <>
            <ProgressBar
              now={(strength.score / 5) * 100}
              variant={strength.variant}
              style={{ height: 4 }}
              className="mb-1"
            />
            <small className={`text-${strength.variant}`}>
              Seguridad: {strength.label}
            </small>
          </>
        )}
      </div>

      <Form.Group className="mb-1" controlId={confirmId}>
        <Form.Label>{confirmLabel}</Form.Label>
        <PasswordInput
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          minLength={8}
          autoComplete={autoComplete}
          placeholder="Repite la contraseña"
          isInvalid={mismatch}
          isValid={matches}
        />
      </Form.Group>

      <div className="mb-3" style={{ minHeight: "1.25rem" }}>
        {mismatch && (
          <small className="text-danger">
            <i className="mdi mdi-close-circle-outline me-1" />
            Las contraseñas no coinciden.
          </small>
        )}
        {matches && (
          <small className="text-success">
            <i className="mdi mdi-check-circle-outline me-1" />
            Las contraseñas coinciden.
          </small>
        )}
      </div>
    </>
  );
}

export { NewPasswordFields, passwordStrength };
